import Icon from "@/components/Icon";
import { Path } from "@/router";
import classNames from "classnames";
import { METRIC_CARD_MIN_HEIGHT_CLASS } from "@/components/MetricGrid/metricGridLayout";
import { MetricCardType } from "./MetricCard.types";
import Tooltip from "@/components/Tooltip";
import { cond } from "lodash";
import { scrollToElement } from "@/lib/scrollHelpers";

export default function MetricCard<P extends Path>({
  iconName,
  label,
  value,
  loading,
  link,
  variant,
  tooltip,
  onClick,
  asButton = false,
  ariaLabel,
  scrollToId,
  onBeforeScroll,
  testId = "metric",
  trendLabel,
  trendDirection
}: MetricCardType<P>) {
  const isEmpty = !value;
  const valueClasses = classNames("text-sys-brand-on-surface", {
    "text-body-xlarge-bold": true,
    "opacity-20": isEmpty
  });
  const displayIconName = iconName ?? (link ? "error" : undefined);

  const handleClick = () => {
    if (loading) {
      return;
    }

    if (onClick) {
      onClick();
    } else if (scrollToId) {
      if (onBeforeScroll) {
        onBeforeScroll();
      } else {
        scrollToElement(scrollToId);
      }
    }
  };

  const hasLinkOrOnClick = !!(link || onClick);
  const isClickable = !loading && (hasLinkOrOnClick || !!scrollToId);
  const isActionButton = Boolean(onClick) && !link && !tooltip && asButton;

  const getVariantBadge = cond([
    [
      (v: string) => v === "success",
      () => (
        <div className="absolute top-6 left-7 w-6 h-6 rounded-full bg-extended-success-brand-color flex items-center justify-center border-2 border-extended-success-brand-on-color">
          <Icon name="check" size={16} className="text-extended-success-brand-on-color" />
        </div>
      )
    ],
    [
      (v: string) => v === "error",
      () => (
        <div className="absolute top-6 left-7 w-6 h-6 rounded-full bg-sys-brand-error flex items-center justify-center border-2 border-sys-brand-on-error">
          <Icon name="priority_high" size={16} className="text-sys-brand-on-error" />
        </div>
      )
    ],
    [
      (v: string) => v === "warn",
      () => (
        <div className="absolute top-6 left-7 w-6 h-6 rounded-full bg-extended-warning-brand-color flex items-center justify-center border-2 border-extended-warning-brand-on-color">
          <Icon name="info_i" size={16} className="text-extended-warning-brand-on-color" />
        </div>
      )
    ],
    [
      (v: string) => v === "info",
      () => (
        <div className="absolute top-6 left-7 w-6 h-6 rounded-full bg-extended-pganavy-brand-color-container-bright flex items-center justify-center border-2 border-extended-pganavy-brand-on-color">
          <Icon name="info_i" size={16} className="text-extended-pganavy-brand-on-color-container" />
        </div>
      )
    ],
    [() => true, () => null] // default case
  ]);

  const className = classNames(
    "p-4 flex flex-col h-full rounded-lg transition-colors duration-200 ease-in-out min-w-0 w-full",
    METRIC_CARD_MIN_HEIGHT_CLASS,
    isActionButton && "text-left",
    hasLinkOrOnClick ? "border-2 border-extended-pgadarknavy-brand-color" : "border border-sys-brand-outline",
    isClickable && "cursor-pointer hover:bg-extended-pgasilver-brand-color hover:bg-opacity-40"
  );
  const accessibilityLabel = ariaLabel ?? (loading ? label : undefined);
  const cardProps = {
    className,
    "data-testid": testId,
    "aria-busy": loading || undefined,
    "aria-label": accessibilityLabel,
    onClick: handleClick
  } as const;

  const body = (
    <>
      <div className="flex justify-between items-start">
        {displayIconName && (
          <div className="relative w-10 h-10 mb-4">
            {loading ? (
              <div className="w-10 h-10 rounded-[6px] animate-pulse bg-gradient-to-r from-gray-200 to-gray-50" />
            ) : (
              <div
                className={classNames(
                  "w-10 h-10 rounded-[6px] items-center justify-center flex flex-shrink-0",
                  hasLinkOrOnClick ? "bg-extended-pgadarknavy-brand-color" : "bg-extended-pgasilver-brand-color"
                )}
              >
                <Icon
                  name={displayIconName}
                  size={24}
                  className={
                    hasLinkOrOnClick
                      ? "text-extended-pgadarknavy-brand-on-color"
                      : "text-extended-pgasilver-brand-on-color"
                  }
                />
              </div>
            )}
            {variant && !loading && getVariantBadge(variant)}
          </div>
        )}
        {tooltip && hasLinkOrOnClick && (
          <div className="flex items-start">
            <Tooltip
              title={tooltip.title}
              supportingText={tooltip.supportingText}
              actions={tooltip.actions}
              variant={tooltip.variant}
              color={tooltip.color}
              noVerticalFlip
            >
              <Icon name="info" />
            </Tooltip>
            <Icon name="navigate_next" />
          </div>
        )}
        {tooltip && !hasLinkOrOnClick && (
          <Tooltip
            title={tooltip.title}
            supportingText={tooltip.supportingText}
            actions={tooltip.actions}
            variant={tooltip.variant}
            color={tooltip.color}
            noVerticalFlip
          >
            <Icon name="info" />
          </Tooltip>
        )}
        {!tooltip && hasLinkOrOnClick && <Icon name="navigate_next" />}
      </div>
      {loading ? (
        <div className="flex flex-col gap-2 mt-1" data-testid="metric-loading" aria-hidden="true">
          <div className="animate-pulse h-6 rounded-full bg-gradient-to-r from-gray-200 to-gray-50 w-full max-w-[160px]" />
          <div className="animate-pulse h-4 rounded-full bg-gradient-to-r from-gray-200 to-gray-50 w-32" />
          <div className="animate-pulse h-4 rounded-full bg-gradient-to-r from-gray-200 to-gray-50 w-24" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <div className={valueClasses}>{isEmpty ? "--" : value}</div>
            {trendLabel && trendDirection ? (
              <p
                className={classNames(
                  "flex items-center gap-1 text-body-medium",
                  trendDirection === "down" ? "text-sys-brand-error" : "text-extended-success-brand-color"
                )}
              >
                <Icon name={trendDirection === "down" ? "trending_down" : "trending_up"} size={18} aria-hidden />
                {trendLabel}
              </p>
            ) : null}
          </div>
          <div className="text-body-medium min-h-[24px] leading-tight whitespace-pre-line">{label}</div>
        </>
      )}
    </>
  );

  if (isActionButton) {
    return (
      <button type="button" disabled={loading} {...cardProps}>
        {body}
      </button>
    );
  }

  return <div {...cardProps}>{body}</div>;
}
